import PageShell from '@/pages/shared/PageShell';

export default function AiGantt() {
  return (
    <PageShell
      title="AIガントチャート｜自動生成される工程表でプロジェクト管理｜”おもいむすび”"
      description="AIがタスクからガントチャートを自動生成。依存関係の矢印、マイルストーン、ベースライン比較、予実比較、遅延タスク表示。ドラッグ＆ドロップで簡単に並び替え可能です。"
      keywords="AI ガントチャート,ガントチャート 自動生成 AI,AI 工程表,プロジェクト管理 ガントチャート ツール,工程表 自動作成 AI"
      canonical="https://omoimusubi.asia/features/ai-gantt/"
      h1="AIが作る、更新しやすいプロジェクト管理ガントチャート"
      subtitle="AIが提案したタスクをもとに、ガントチャートを自動生成。依存関係、マイルストーン、予定と実績の比較を自動で表示します。"
      relatedLinks={[
        { label: 'AIタスク分解', path: '/features/ai-task-breakdown/' },
        { label: 'ガントチャートとは？作り方とツールの選び方', path: '/guides/gantt-chart/' },
        { label: 'WBSとガントチャートの違い', path: '/guides/wbs-and-gantt-chart/' },
        { label: 'ガントチャートツール比較', path: '/compare/gantt-chart-tools/' },
      ]}
    >
      <div className="not-prose mb-8 rounded-xl overflow-hidden border border-background-200/70">
        <img
          src="https://readdy.ai/api/search-image?query=A%20bright%20modern%20Japanese%20office%20meeting%20scene%20two%20Japanese%20business%20professionals%20a%20man%20and%20a%20woman%20in%20their%2030s%20standing%20in%20front%20of%20a%20large%20wall%20mounted%20display%20monitor%20showing%20a%20colorful%20detailed%20gantt%20chart%20with%20horizontal%20task%20bars%20milestone%20diamond%20markers%20and%20dependency%20arrows%20the%20woman%20is%20pointing%20at%20the%20screen%20explaining%20the%20project%20timeline%20natural%20daylight%20filling%20the%20room%20through%20glass%20walls%20green%20plants%20wooden%20accents%20refreshing%20clean%20contemporary%20Japanese%20workplace%20atmosphere%20professional%20yet%20relaxed&width=1200&height=700&seq=feature-ai-gantt-01&orientation=landscape&nocache=true"
          alt="AIガントチャートの製品画面 - 依存関係・マイルストーン・進捗が一目で分かるガントチャート"
          className="w-full h-auto"
        />
      </div>

      <div className="not-prose my-8 bg-background-100 rounded-lg border border-background-200/70 p-6 overflow-x-auto">
        <h2 className="text-base font-bold text-foreground-900 mb-4">AIガントチャート画面イメージ</h2>
        <div className="min-w-[700px]">
          <div className="flex items-center gap-2 mb-4 text-xs">
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-medium">予定通り</span>
            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded font-medium">やや遅延</span>
            <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-medium">遅延</span>
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-medium">◆ マイルストーン</span>
          </div>
          <div className="space-y-1.5">
            {/* Header */}
            <div className="grid grid-cols-[200px_repeat(8,1fr)] gap-0 text-[10px] text-foreground-500 border-b border-background-300 pb-1">
              <span className="font-medium text-foreground-800">タスク名</span>
              <span>8/1</span><span>8/8</span><span>8/15</span><span>8/22</span><span>8/29</span><span>9/5</span><span>9/12</span><span>9/19</span>
            </div>
            {/* Task rows */}
            {[
              { name: '要件定義書作成', start: 0, len: 3, status: 'green', dep: null },
              { name: '基本設計書作成', start: 2, len: 4, status: 'green', dep: '要件定義書' },
              { name: '詳細設計書作成', start: 5, len: 5, status: 'amber', dep: '基本設計書' },
              { name: '画面デザイン作成', start: 2, len: 4, status: 'green', dep: null },
              { name: 'フロントエンド開発', start: 5, len: 6, status: 'green', dep: '画面デザイン' },
              { name: 'バックエンド開発', start: 5, len: 6, status: 'green', dep: '詳細設計書' },
              { name: 'テスト実施', start: 10, len: 3, status: 'red', dep: '開発完了' },
              { name: '◆ リリース', start: 12, len: 1, status: 'milestone', dep: 'テスト完了' },
            ].map((task, i) => (
              <div key={i} className="grid grid-cols-[200px_repeat(8,1fr)] gap-0 items-center text-[10px]">
                <span className={`truncate font-medium ${task.status === 'red' ? 'text-red-700' : task.status === 'amber' ? 'text-amber-700' : 'text-foreground-700'}`}>
                  {task.dep && <span className="text-foreground-400 mr-0.5">→</span>}{task.name}
                </span>
                {Array.from({ length: 8 }).map((_, j) => {
                  const isActive = j >= task.start && j < task.start + task.len;
                  if (!isActive) return <div key={j} className="h-4"></div>;
                  const isFirst = j === task.start;
                  const bg = task.status === 'milestone' ? 'bg-primary-500' : task.status === 'red' ? 'bg-red-400' : task.status === 'amber' ? 'bg-amber-400' : 'bg-green-400';
                  return (
                    <div key={j} className={`h-3 ${isFirst ? 'rounded-l-full' : ''} ${j === task.start + task.len - 1 ? 'rounded-r-full' : ''} ${bg} ${j === task.start ? 'ml-0.5' : ''}`}></div>
                  );
                })}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-foreground-500 mt-4">※ 実際の画面では、ドラッグ＆ドロップでの並び替え、担当者別フィルタ、今日やることの自動抽出が可能です。</p>
        </div>
      </div>

      <section>
        <h2>AIガントチャートの主な機能</h2>
        <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-4 my-4">
          {[
            { icon: 'ri-organization-chart', title: 'タスクの親子関係', desc: '大項目と小項目を階層的に表示し、プロジェクト全体の構造を把握できます。' },
            { icon: 'ri-git-branch-line', title: '依存関係の矢印', desc: 'タスク間の依存関係を矢印で視覚化。遅延の影響が一目で分かります。' },
            { icon: 'ri-star-line', title: 'マイルストーン', desc: 'プロジェクトの重要な節目をひし形で表示します。' },
            { icon: 'ri-contrast-line', title: 'ベースライン比較', desc: '当初計画と変更後の計画を比較表示します。' },
            { icon: 'ri-bar-chart-2-line', title: '予定と実績の比較', desc: '各タスクの予定日数と実際の所要日数を比較表示します。' },
            { icon: 'ri-drag-move-line', title: 'ドラッグ＆ドロップ', desc: 'マウス操作でタスクの順序や期間を直感的に変更できます。' },
            { icon: 'ri-alert-line', title: '遅延タスクの表示', desc: '期限超過や進捗遅れのタスクを強調表示します。' },
            { icon: 'ri-user-line', title: '担当者別・プロジェクト別表示', desc: 'メンバーごと、プロジェクトごとに表示を切り替えられます。' },
            { icon: 'ri-calendar-check-line', title: '今日やることの自動抽出', desc: '各メンバーが今日取り組むべきタスクを自動で一覧表示します。' },
            { icon: 'ri-refresh-line', title: '進捗の自動更新', desc: '日報と連動し、AIがタスクの進捗率を自動で更新します。' },
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-3 bg-background-100 rounded-lg p-4 border border-background-200/70">
              <div className="w-9 h-9 bg-primary-100 rounded-lg flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-primary-600 text-sm`}></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground-900">{item.title}</h3>
                <p className="text-xs text-foreground-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2>ガントチャートが自動で更新される仕組み</h2>
        <p>”おもいむすび”では、メンバーが日報を入力するだけで、AIが内容を解析し、対応するタスクの進捗率やステータスを自動更新します。手動での更新作業が不要になり、常に最新の状態がガントチャートに反映されます。</p>
      </section>

      <section>
        <h2>よくある質問</h2>
        <h3>ガントチャートをExcelにエクスポートできますか</h3>
        <p>はい。”おもいむすび”で作成したガントチャートは、ExcelやPDF形式でエクスポートできます。ステークホルダーへの共有や、会議資料としての活用に便利です。</p>
        <h3>複数プロジェクトを同時にガントチャート表示できますか</h3>
        <p>各プロジェクトのガントチャートは個別に表示されますが、プロジェクト横断のダッシュボードで全プロジェクトの進捗状況を一覧できます。</p>
      </section>
    </PageShell>
  );
}